'use client';

export type RegionCode = 'IN' | 'US' | 'GB' | 'EU' | 'AU' | 'ROW';

export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  region: RegionCode;
  country: string;
  locale: string;
  gateway: string;
  plans: { free: string; artistPro: string; label: string };
  plansRaw: { artistPro: number; label: number };
  usdEquivalent: number; // 1 unit = X USD
}

export const CURRENCY_MAP: Record<string, CurrencyConfig> = {
  IN: {
    code: 'INR', symbol: '₹', name: 'Indian Rupee', region: 'IN', country: 'India',
    locale: 'en-IN', gateway: 'Razorpay / UPI / NetBanking',
    plans: { free: '₹0', artistPro: '₹1,499', label: '₹3,499' },
    plansRaw: { artistPro: 1499, label: 3499 },
    usdEquivalent: 0.012,
  },
  US: {
    code: 'USD', symbol: '$', name: 'US Dollar', region: 'US', country: 'United States',
    locale: 'en-US', gateway: 'Stripe / PayPal',
    plans: { free: '$0', artistPro: '$19.99', label: '$44.99' },
    plansRaw: { artistPro: 19.99, label: 44.99 },
    usdEquivalent: 1,
  },
  GB: {
    code: 'GBP', symbol: '£', name: 'British Pound', region: 'GB', country: 'United Kingdom',
    locale: 'en-GB', gateway: 'Stripe',
    plans: { free: '£0', artistPro: '£15.99', label: '£35.99' },
    plansRaw: { artistPro: 15.99, label: 35.99 },
    usdEquivalent: 1.27,
  },
  EU: {
    code: 'EUR', symbol: '€', name: 'Euro', region: 'EU', country: 'Europe',
    locale: 'de-DE', gateway: 'Stripe / SEPA',
    plans: { free: '€0', artistPro: '€18.99', label: '€39.99' },
    plansRaw: { artistPro: 18.99, label: 39.99 },
    usdEquivalent: 1.09,
  },
  AU: {
    code: 'AUD', symbol: 'A$', name: 'Australian Dollar', region: 'AU', country: 'Australia',
    locale: 'en-AU', gateway: 'Stripe',
    plans: { free: 'A$0', artistPro: 'A$29.99', label: 'A$64.99' },
    plansRaw: { artistPro: 29.99, label: 64.99 },
    usdEquivalent: 0.65,
  },
  ROW: {
    code: 'USD', symbol: '$', name: 'US Dollar', region: 'ROW', country: 'Rest of World',
    locale: 'en-US', gateway: 'Stripe / PayPal',
    plans: { free: '$0', artistPro: '$19.99', label: '$44.99' },
    plansRaw: { artistPro: 19.99, label: 44.99 },
    usdEquivalent: 1,
  },
};

export const EU_COUNTRIES = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'SE', 'DK', 'FI', 'NO', 'PL', 'CZ', 'HU', 'RO', 'GR', 'CH'];

export function getRegionFromCountry(countryCode: string): string {
  if (countryCode === 'IN') return 'IN';
  if (countryCode === 'US') return 'US';
  if (countryCode === 'GB') return 'GB';
  if (countryCode === 'AU') return 'AU';
  if (EU_COUNTRIES.includes(countryCode)) return 'EU';
  return 'ROW';
}

export function getCurrencyConfig(regionOrCountry: string): CurrencyConfig {
  const region = getRegionFromCountry(regionOrCountry);
  return CURRENCY_MAP[region] || CURRENCY_MAP['ROW'];
}

export function formatCurrency(amount: number, config: CurrencyConfig): string {
  return `${config.symbol}${amount.toLocaleString(config.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function toUSD(amount: number, config: CurrencyConfig): string {
  const usd = amount * config.usdEquivalent;
  return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const COUNTRY_LIST = [
  { code: 'IN', name: 'India', region: 'IN' },
  { code: 'US', name: 'United States', region: 'US' },
  { code: 'GB', name: 'United Kingdom', region: 'GB' },
  { code: 'AU', name: 'Australia', region: 'AU' },
  { code: 'DE', name: 'Germany', region: 'EU' },
  { code: 'FR', name: 'France', region: 'EU' },
  { code: 'IT', name: 'Italy', region: 'EU' },
  { code: 'ES', name: 'Spain', region: 'EU' },
  { code: 'NL', name: 'Netherlands', region: 'EU' },
  { code: 'CA', name: 'Canada', region: 'ROW' },
  { code: 'BR', name: 'Brazil', region: 'ROW' },
  { code: 'NG', name: 'Nigeria', region: 'ROW' },
  { code: 'ZA', name: 'South Africa', region: 'ROW' },
  { code: 'KE', name: 'Kenya', region: 'ROW' },
  { code: 'JP', name: 'Japan', region: 'ROW' },
  { code: 'KR', name: 'South Korea', region: 'ROW' },
  { code: 'SG', name: 'Singapore', region: 'ROW' },
  { code: 'AE', name: 'UAE', region: 'ROW' },
  { code: 'OTHER', name: 'Other', region: 'ROW' },
];

export const PAYOUT_METHODS: Record<string, { methods: string[]; details: string[] }> = {
  IN: {
    methods: ['Bank Transfer (NEFT/RTGS/IMPS)', 'UPI ID'],
    details: ['Account Number + IFSC Code', 'UPI ID (e.g. name@upi)'],
  },
  US: {
    methods: ['PayPal', 'Stripe Direct Payout', 'Wise', 'International Wire (SWIFT/ACH)'],
    details: ['PayPal email', 'Stripe account', 'Wise email', 'SWIFT + Account Number'],
  },
  GB: {
    methods: ['PayPal', 'Wise', 'International Wire (SWIFT/IBAN)'],
    details: ['PayPal email', 'Wise email', 'IBAN + BIC/SWIFT'],
  },
  EU: {
    methods: ['PayPal', 'Wise', 'SEPA Bank Transfer', 'International Wire (IBAN/BIC)'],
    details: ['PayPal email', 'Wise email', 'IBAN', 'IBAN + BIC'],
  },
  AU: {
    methods: ['PayPal', 'Wise', 'International Wire (SWIFT)'],
    details: ['PayPal email', 'Wise email', 'BSB + Account Number'],
  },
  ROW: {
    methods: ['PayPal', 'Wise', 'International Wire (SWIFT/IBAN/BIC)'],
    details: ['PayPal email', 'Wise email', 'SWIFT + IBAN + BIC'],
  },
};

export const TAX_FIELDS: Record<string, { label: string; placeholder: string; key: string; required?: boolean }[]> = {
  IN: [
    { label: 'PAN Card Number', placeholder: 'e.g. ABCDE1234F', key: 'panNumber', required: true },
    { label: 'GST Number (optional)', placeholder: 'e.g. 27ABCDE1234F1Z5', key: 'gstNumber' },
  ],
  US: [
    { label: 'SSN / EIN', placeholder: 'e.g. 123-45-6789', key: 'ssnEin', required: true },
    { label: 'Tax Form Type', placeholder: 'W-9 (US Citizen) or W-8BEN (Foreign)', key: 'taxFormType', required: true },
  ],
  GB: [
    { label: 'VAT Number (optional)', placeholder: 'e.g. GB123456789', key: 'vatNumber' },
    { label: 'National Insurance Number', placeholder: 'e.g. AB123456C', key: 'niNumber' },
  ],
  EU: [
    { label: 'VAT Number', placeholder: 'e.g. DE123456789', key: 'vatNumber' },
    { label: 'National Tax ID', placeholder: 'Country-specific tax ID', key: 'nationalTaxId' },
  ],
  AU: [
    { label: 'ABN (Australian Business Number)', placeholder: 'e.g. 51 824 753 556', key: 'abn' },
    { label: 'TFN (Tax File Number)', placeholder: 'e.g. 123 456 789', key: 'tfn' },
  ],
  ROW: [
    { label: 'National Tax ID (TIN)', placeholder: 'Your country\'s tax identification number', key: 'tin', required: true },
  ],
};
